import type { ReactNode } from 'react'

import Image from 'next/image'
import { ArrowUpRight } from 'lucide-react'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { Marquee } from '@/components/generals/marquee'
import { socialHandle, socialPosts } from '@/constants/home'
import { siteConfig } from '@/constants/site'

const SocialTile = ({ handle, image }: { handle: string; image: string }): ReactNode => (
    <a
        href={siteConfig.links.instagram}
        target='_blank'
        rel='noreferrer noopener'
        className='group relative mx-1.5 block aspect-[4/5] w-[180px] shrink-0 overflow-hidden rounded-xl bg-shell md:w-[220px]'
    >
        <Image
            src={image}
            alt={`Instagram post from ${handle}`}
            fill
            sizes='220px'
            className='object-cover transition-transform duration-[900ms] ease-nuvea group-hover:scale-[1.08]'
        />

        <span
            aria-hidden='true'
            className='absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 ease-nuvea group-hover:opacity-100'
        />

        <span className='type-micro absolute bottom-3 left-3 translate-y-2 text-cream opacity-0 transition-[opacity,transform] duration-500 ease-nuvea group-hover:translate-y-0 group-hover:opacity-100'>
            {handle}
        </span>
    </a>
)

export const Social = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, scope: root, reduced }) => {
        if (reduced) return

        gsap.from(select('[data-social-heading] .reveal-mask > span'), {
            yPercent: 115,
            duration: 1,
            stagger: 0.05,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 78%' }
        })

        gsap.from(select('[data-social-meta] > *'), {
            y: 20,
            opacity: 0,
            duration: 0.85,
            stagger: 0.08,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 78%' }
        })

        // Both belts fade up together, then drift in opposite directions.
        gsap.from(select('[data-social-belt]'), {
            opacity: 0,
            y: 40,
            duration: 1.1,
            stagger: 0.12,
            ease: 'nuvea',
            scrollTrigger: {
                trigger: select('[data-social-belts]')[0],
                start: 'top 88%'
            }
        })
    })

    const firstHalf = socialPosts.slice(0, 6)
    const secondHalf = socialPosts.slice(6)

    return (
        <section ref={scope} id='social' className='overflow-hidden bg-shell py-24 md:py-32'>
            <div className='shell-x mx-auto flex w-full max-w-[1760px] flex-col gap-8 md:flex-row md:items-end md:justify-between'>
                <SplitWords
                    data-social-heading=''
                    as='h2'
                    lines={['Quietly,', 'on social.']}
                    className='type-h1 max-w-[16ch] text-ink'
                />

                <div data-social-meta='' className='flex flex-col items-start gap-3 md:items-end'>
                    <p className='type-body text-slate-soft'>
                        {socialHandle}
                    </p>

                    <a
                        href={siteConfig.links.instagram}
                        target='_blank'
                        rel='noreferrer noopener'
                        className='type-small group inline-flex items-center gap-2 rounded-full bg-sage px-5 py-3 text-ink transition-[background-color,color] duration-400 ease-nuvea hover:bg-ink hover:text-cream'
                    >
                        Follow

                        <ArrowUpRight
                            className='size-4 transition-transform duration-400 ease-nuvea group-hover:translate-x-0.5 group-hover:-translate-y-0.5'
                            strokeWidth={1.6}
                        />
                    </a>
                </div>
            </div>

            <div data-social-belts='' className='mt-12 flex flex-col gap-3 md:mt-16'>
                <div data-social-belt=''>
                    <Marquee duration={58}>
                        {firstHalf.map((post) => (
                            <SocialTile key={post.handle} handle={post.handle} image={post.image} />
                        ))}
                    </Marquee>
                </div>

                <div data-social-belt=''>
                    <Marquee duration={64} direction='right'>
                        {secondHalf.map((post) => (
                            <SocialTile key={post.handle} handle={post.handle} image={post.image} />
                        ))}
                    </Marquee>
                </div>
            </div>
        </section>
    )
}
